import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

import styles from './Button.module.css';

/**
 * Button — the reference primitive.
 *
 * Handoff, "Components / Buttons": five variants, one height driven by density,
 * an accent outline rather than a flooded fill, and copy that is verb + object.
 *
 * Three decisions worth copying into tasks 21-25:
 *
 * 1. **`disabled` and `loading` are different states and take different
 *    attributes.** `disabled` is the native attribute: the control is not
 *    available, and it leaves the tab order. `loading` is `aria-disabled` +
 *    `aria-busy`: the control is temporarily working, and it MUST stay
 *    focusable — a button that is blurred the instant it becomes busy announces
 *    its busy state to nobody, because the element the screen reader was on has
 *    just left the accessibility tree.
 *
 * 2. **`aria-disabled` is a promise to assistive technology, not an instruction
 *    to the browser.** A busy `type="submit"` button still submits its form on
 *    Enter unless the component says otherwise. The click guard below is
 *    load-bearing, not defensive.
 *
 * 3. **The component renders no strings.** Every word comes from the caller,
 *    which is where `useTranslation()` lives. A primitive that hard-codes
 *    "Loading…" is a primitive that ships English to an Arabic agent.
 *
 * The visual contract lives entirely in Button.module.css and is read from role
 * tokens, so `data-theme="light"` repaints it without this file knowing.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'icon' | 'destructive';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Handoff: one `primary` per screen region. Defaults to `primary`. */
  variant?: ButtonVariant;
  /**
   * The operation is running. Sets `aria-busy` and `aria-disabled`, swaps the
   * icon for the spinner, and swallows activation — without leaving the tab
   * order.
   */
  loading?: boolean;
  /** Leading glyph, 16px, decorative. Hidden from assistive technology. */
  icon?: ReactNode;
  children?: ReactNode;
}

function hasText(children: ReactNode): boolean {
  return children !== undefined && children !== null && children !== false && children !== '';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    loading = false,
    icon,
    children,
    className,
    disabled = false,
    type = 'button',
    onClick,
    ...rest
  },
  ref,
) {
  // An explicit `disabled` outranks `loading`: the caller has said the action is
  // unavailable, which is a stronger statement than "in flight".
  const busy = loading && !disabled;

  if (import.meta.env.DEV && !hasText(children) && !rest['aria-label'] && !rest['aria-labelledby']) {
    console.error(
      'Button: a button with no visible label needs an accessible name. ' +
        'Pass aria-label (or aria-labelledby) — an icon alone is not a name.',
    );
  }

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (busy) {
      // aria-disabled does not stop the browser. Without these two lines a busy
      // submit button submits, and a busy row button still opens its case.
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  }

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={[styles['btn'], styles[variant], className].filter(Boolean).join(' ')}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={busy || undefined}
      onClick={handleClick}
    >
      {busy ? (
        <span className={styles['spinner']} data-testid="buttonSpinner" aria-hidden="true" />
      ) : null}
      {!busy && icon ? (
        <span className={styles['iconSlot']} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
});
